import React, { useState, useMemo } from 'react';
import { Card, Switch } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatVnCalendarDate, formatVnChartDay } from '../../utils/vnDateTime';
import type { TrendDaily } from '../../types/dashboardFounder';
import { fillMissingDatesInRange } from '../../utils/dashboardFounderUtils';
import { ChartSkeleton } from '../dashboard/ChartSkeleton';
import { DashboardEmptyState } from '../dashboard/DashboardEmptyState';

interface FulfillmentTimeChartProps {
  data: TrendDaily[];
  startDate: string;
  endDate: string;
  loading?: boolean;
}

const defaultTrend = (date: string): TrendDaily => ({
  date,
  orders: 0,
  gmv: 0,
  platformFee: 0,
  voucherCost: 0,
  net: 0,
  cancelRate: 0,
  newUsers: 0,
  returningUsers: 0,
  avgCreateToConfirmMin: 0,
  avgConfirmToDeliveredMin: 0,
  avgTotalMin: 0,
});

export const FulfillmentTimeChart: React.FC<FulfillmentTimeChartProps> = ({
  data,
  startDate,
  endDate,
  loading = false,
}) => {
  const [useP90, setUseP90] = useState(false);
  const hasP90 = useMemo(() => data.some(d => d.p90TotalMin != null), [data]);

  const chartData = useMemo(() => {
    const filled = fillMissingDatesInRange(
      data,
      startDate,
      endDate,
      defaultTrend
    );
    return filled.map(d => ({
      ...d,
      label: formatVnChartDay(d.date),
      createToConfirm:
        useP90 && hasP90
          ? d.p90CreateToConfirmMin ?? d.avgCreateToConfirmMin
          : d.avgCreateToConfirmMin,
      confirmToDelivered:
        useP90 && hasP90
          ? d.p90ConfirmToDeliveredMin ?? d.avgConfirmToDeliveredMin
          : d.avgConfirmToDeliveredMin,
      total: useP90 && hasP90 ? d.p90TotalMin ?? d.avgTotalMin : d.avgTotalMin,
    }));
  }, [data, startDate, endDate, useP90, hasP90]);

  if (loading) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <div className="founder-chart-header">
          <h3 className="founder-chart-title">Fulfillment Time</h3>
        </div>
        <ChartSkeleton height={240} />
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Fulfillment Time</h3>
        <DashboardEmptyState
          message="Chưa có dữ liệu"
          suggestion="Chọn khoảng ngày khác."
        />
      </Card>
    );
  }

  return (
    <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
      <div className="founder-chart-header">
        <h3 className="founder-chart-title">Fulfillment Time</h3>
        {hasP90 && (
          <Switch
            size="small"
            checked={useP90}
            onChange={setUseP90}
            checkedChildren="p90"
            unCheckedChildren="p50"
          />
        )}
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            vertical={false}
          />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={v => `${v} phút`} width={50} />
          <Tooltip
            formatter={(value: number) => [`${value.toFixed(1)} phút`, '']}
            labelFormatter={label => {
              const item = chartData.find(d => d.label === label);
              return item
                ? formatVnCalendarDate(item.date, 'DD/MM/YYYY')
                : label;
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="createToConfirm"
            stroke="#8b5cf6"
            strokeWidth={2}
            dot={false}
            name="Create→Confirm (phút)"
          />
          <Line
            type="monotone"
            dataKey="confirmToDelivered"
            stroke="#0ea5e9"
            strokeWidth={2}
            dot={false}
            name="Confirm→Delivered (phút)"
          />
          <Line
            type="monotone"
            dataKey="total"
            stroke="#22c55e"
            strokeWidth={2}
            dot={false}
            name="Total (phút)"
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};
